import { Component, OnInit, OnDestroy } from '@angular/core';
import * as _ from 'lodash';
import * as Chart from 'chart.js';
import { Sidenavaction } from 'src/assets/js/application/sidenavaction';
import { HarvestLogService } from '../../../core/services/harvestlog.service';
import HarvestLogModel from '../../../core/models/harvestlog.model';
import { FarmSiteService } from 'src/app/core/services/farm-site.service';
import FarmSiteModel from 'src/app/core/models/farm-site.model';
import PreferencesModel from 'src/app/core/models/preferences.model';
import { PreferencesService } from 'src/app/core/services/preferences.service';
import PersonsModel from '../../../core/models/persons.model';
import { PersonsService } from '../../../core/services/persons.service';
import { CommonMethodsService } from 'src/app/core/services/common-methods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';

declare var $: any;

/**
 * @component HarvestComponent
 * @selector app-harvest
 * @description
 * The `HarvestComponent` is responsible for displaying harvest-related metrics within the Farm Metrics dashboard.
 * It focuses on visualizing two primary datasets:
 * 1. **Harvest DOC (Days of Culture)**: Distribution of harvested ponds based on the duration of the culture.
 * 2. **Survival Rate (SR)**: Distribution of harvested ponds based on the survival rate of the stock.
 *
 * ## Key Responsibilities:
 * - **Data Aggregation**: Fetches harvest logs and aggregates data for DOC and Survival Rate summaries.
 * - **Visualization**: Renders interactive charts (Doughnut or Bar) using Chart.js to display metric distributions.
 * - **Role-Based Access**: Adapts data retrieval based on the logged-in user's role (Farmer, Farm Manager, Provider, etc.).
 * - **User Preferences**: Persists user's preferred chart type (Bar vs Doughnut) for each metric.
 * - **Detailed Reporting**: Displays tabular lists of harvest details alongside the charts.
 *
 * ## User Roles & Permissions:
 * - **Farmer / Business Admin (Farm)**: View harvest metrics for their specific farm business.
 * - **Farm Manager**: View metrics for farms assigned to them.
 * - **Business Admin (Provider)**: View metrics for farms associated with the provider.
 * - **Provider Manager**: View metrics for farms assigned to their technicians.
 * - **Employee**: View metrics for all farm sites (internal admin).
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessName`: Business name.
 * - `loginBusinessCategory`: Business category.
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `docType`: Saved preference for DOC chart type.
 * - `srType`: Saved preference for Survival Rate chart type.
 *
 * ## Data Flow:
 * 1. **Initialization (`ngOnInit`)**:
 *    - Identifies user context from `localStorage`.
 *    - Fetches relevant farm sites based on role.
 *    - Calls `getOnLoadCallsData` with farm site IDs.
 * 2. **Data Fetching (`getOnLoadCallsData`)**:
 *    - Fetches DOC summary, SR summary, SR details, and DOC details concurrently.
 *    - Prepares chart data (`piekeys`, `piedata`, `srpiekeys`, `srpiedata`).
 *    - Renders charts (`prepareDOCChart`, `prepareSRChart`).
 * 3. **Interaction**:
 *    - User toggles chart types via `docgraphselection` or `srgraphselection`.
 *    - User searches lists via `searchBusiness` or `searchDocBusiness`.
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-harvest',
  templateUrl: './harvest.component.html',
  styleUrls: ['./harvest.component.scss']
})
export class HarvestComponent implements OnInit, OnDestroy {
  /** @type {Array<FarmSiteModel>} List of farm sites associated with the user. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {Array<PreferencesModel>} User preferences for chart display settings. */
  public preferences: Array<PreferencesModel>;

  /** @type {Array<HarvestLogModel>} List of harvest logs (summary data). */
  public harvestlogs: Array<HarvestLogModel>;

  /** @type {Array<HarvestLogModel>} List of harvest logs grouped by Survival Rate location. */
  public srLocations: Array<HarvestLogModel>;

  /** @type {Array<HarvestLogModel>} List of harvest logs grouped by DOC location. */
  public docLocations: Array<HarvestLogModel>;

  /** @type {Array<HarvestLogModel>} Detailed list of harvest logs for Survival Rate view. */
  public survivalRateDetails: Array<HarvestLogModel>;

  /** @type {Array<HarvestLogModel>} Detailed list of harvest logs for DOC view. */
  public docDetailsList: Array<HarvestLogModel>;

  /** @type {Array<PersonsModel>} List of technicians (for Provider Manager role). */
  public technicians: Array<PersonsModel>;

  /** @type {boolean} Flag indicating if the page data has loaded. */
  public pgLoad = false;

  /** @type {object} Keys (labels) for the DOC chart. */
  public piekeys: object;

  /** @type {object} Data values for the DOC chart. */
  public piedata: object;

  /** @type {string} Title for the DOC chart. */
  public charttitle: string;

  /** @type {object} Keys (labels) for the Survival Rate chart. */
  public srpiekeys: object;

  /** @type {object} Data values for the Survival Rate chart. */
  public srpiedata: object;

  /** @type {string} Title for the Survival Rate chart. */
  public srcharttitle: string;

  /** @type {string} Role of the logged-in user. */
  public loginPersonType: string;

  /** @type {Array<any>} List of farm site IDs used for data fetching. */
  public farmSiteIds = new Array();

  /** @type {Array<any>} List of technician IDs (for Provider Manager). */
  public technicianIds = new Array();

  /** @type {string} Current chart type for DOC (e.g., 'bar', 'doughnut'). */
  public docType: string;

  /** @type {any} Configuration object for the DOC chart. */
  public docgraphconfig: any;

  /** @type {any} Chart.js instance for the DOC chart. */
  public docgraph: any;

  /** @type {string} Current chart type for Survival Rate (e.g., 'bar', 'doughnut'). */
  public srType: string;

  /** @type {any} Configuration object for the Survival Rate chart. */
  public srgraphconfig: any;

  /** @type {any} Chart.js instance for the Survival Rate chart. */
  public srgraph: any;

  /** @type {number} Total sum of pie data values (unused in current logic). */
  public piedataTotal: number;

  /** @type {string} Default chart type if preference not found. */
  public chartType: string;

  /** @type {boolean} Flag indicating if the initial response has loaded. */
  public responseLoad: boolean;

  /** @type {string} Search keyword for Survival Rate list. */
  public search: string;

  /** @type {string} Search keyword for DOC list. */
  public docsearch: string;

  /**
   * @constructor
   * @description Initializes the component and services.
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @param {PersonsService} personService - Service for person-related operations.
   * @param {FarmSiteService} farmSiteService - Service for farm site operations.
   * @param {PreferencesService} preferencesService - Service for managing user preferences.
   * @param {HarvestLogService} harvestLogService - Service for fetching harvest log data.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking previous routes.
   */
  constructor(public sidenavaction: Sidenavaction, public personService: PersonsService, public farmSiteService: FarmSiteService, public preferencesService: PreferencesService, public harvestLogService: HarvestLogService, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) { }

  /**
   * @method ngOnInit
   * @lifecycle angular
   * @async
   * @description
   * Lifecycle hook that initializes the component.
   * 1. Sets the previous route.
   * 2. Retrieves user context from localStorage.
   * 3. Determines the chart type preferences for DOC and SR.
   * 4. Fetches farm sites based on the user's role and business category.
   * 5. Calls `getOnLoadCallsData` to fetch metrics data.
   *
   * @returns {Promise<void>}
   */
  public async ngOnInit() {
    try {
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      // loginperson type details
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.farmSiteIds = new Array();
      if (this.loginPersonType != 'Business Admin') {
        this.docType = localStorage.getItem('docType');
        this.srType = localStorage.getItem('srType');
        if (this.docType == '') {
          this.chartType = 'bar';
        }
        if (this.srType == '') {
          this.chartType = 'bar';
        }
      }
      // get person list based on login businessid
      if (this.loginPersonType == 'Business Admin') {
        this.farmSites = await this.farmSiteService.getFarmeSitesByBAdminId(localStorage.businessID);
        this.farmSiteIds = this.farmSites.map(farmsite => farmsite.id);
        this.getOnLoadCallsData(this.farmSiteIds);
      } else {
        this.technicians = await this.personService.getAllTechniciansByProviderManagerId(localStorage.personID);
        this.technicianIds = this.technicians.map(technician => technician.id);
        this.farmSites = await this.farmSiteService.getFarmeSitesByTechnicianIDs(this.technicianIds);
        this.farmSiteIds = this.farmSites.map(farmsite => farmsite.id);
        this.getOnLoadCallsData(this.farmSiteIds);
      }
      this.responseLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method ngOnDestroy
   * @lifecycle angular
   * @description
   * Lifecycle hook that cleans up resources when the component is destroyed.
   * Cancels ongoing HTTP requests for FarmSite, HarvestLog, and Preferences services.
   *
   * @returns {void}
   */
  public ngOnDestroy(): void {
    this.farmSiteService.cancelOnGoingHttpRequests();
    this.harvestLogService.cancelOnGoingHttpRequests();
    this.preferencesService.cancelOnGoingHttpRequests();
  }

  /**
   * @method getChattypeinDB
   * @async
   * @description
   * Fetches user preferences for chart types from the database.
   * Updates the chart type variables and re-renders charts if preferences exist.
   * @returns {Promise<void>}
   */
  public async getChattypeinDB() {
    try {
      let preferences = await this.preferencesService.getPeferencesbyUserIDBusinessID(localStorage.businessID, localStorage.userID);
      if (preferences.length > 0) {
        this.chartType = preferences[0].chartType;
      } else {
        this.chartType = 'doughnut';
      }
      this.prepareDOCChart(this.chartType);
      this.prepareSRChart(this.chartType);
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getOnLoadCallsData
   * @async
   * @description
   * Fetches harvest metrics data for the provided farm sites.
   * 1. Calls `getHarvestDOCSummary`, `getSurvivalRateSummary`, `getSurvivalRateDetails`, and `getHarvestedDOCDetails` concurrently.
   * 2. Processes summary data for DOC and SR charts.
   * 3. Loads user preferences for chart type if applicable.
   * 4. Renders the charts.
   *
   * @param {Array<any>} farmSiteIds - List of farm site IDs to fetch data for.
   * @returns {Promise<void>}
   */
  public async getOnLoadCallsData(farmSiteIds) {
    try {
      let promiseResults = new Array();
      await Promise.all([
        await this.harvestLogService.getHarvestDOCSummary(farmSiteIds),
        await this.harvestLogService.getSurvivalRateSummary(farmSiteIds),
        await this.harvestLogService.getSurvivalRateDetails(farmSiteIds),
        await this.harvestLogService.getHarvestedDOCDetails(farmSiteIds)

      ]).then((resolve) => {
        promiseResults = resolve;
      }).catch((reject) => {
        alert(reject);
      });
      this.harvestlogs = promiseResults[0];
      if (this.harvestlogs !== undefined) {
        let chartdataobj = Object.assign({}, this.harvestlogs);
        delete chartdataobj['pondsCount'];
        this.piekeys = Object.keys(chartdataobj);
        this.piedata = Object.values(chartdataobj);
        this.piedataTotal = _.sum(this.piedata);
        this.pgLoad = true;
        if (this.loginPersonType == 'Business Admin') {
          this.getChattypeinDB();
        } else {
          let doccharttype = this.docType || 'doughnut';
          this.prepareDOCChart(doccharttype);
        }
      }
      this.harvestlogs = promiseResults[1];
      if (this.harvestlogs !== undefined) {
        let srchartdataobj = Object.assign({}, this.harvestlogs);
        delete srchartdataobj['pondsCount'];
        this.srpiekeys = Object.keys(srchartdataobj);
        this.srpiedata = Object.values(srchartdataobj);
        if (this.loginPersonType == 'Business Admin') {
          this.getChattypeinDB();
        } else {
          let srcharttype = this.srType || 'doughnut';
          this.prepareSRChart(srcharttype);
        }
        this.pgLoad = true;
      }
      this.survivalRateDetails = promiseResults[2];
      this.docDetailsList = promiseResults[3];
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getAllSRLocation
   * @async
   * @description
   * Fetches Survival Rate location summary data.
   * Populates `srLocations`.
   * @returns {Promise<void>}
   */
  public async getAllSRLocation() {
    try {
      this.srLocations = await this.harvestLogService.getSurvivalRateLocation(this.farmSiteIds);
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getAllDocLocation
   * @async
   * @description
   * Fetches Harvest DOC location summary data.
   * Populates `docLocations`.
   * @returns {Promise<void>}
   */
  public async getAllDocLocation() {
    try {
      this.docLocations = await this.harvestLogService.getHarvestDOCLocation(this.farmSiteIds);
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method prepareDOCChart
   * @description
   * Configures and renders the Harvest DOC chart using Chart.js.
   * Supports both Doughnut and Bar chart types based on the `doctype` parameter.
   * Handles "No Data" scenarios.
   *
   * @param {string} doctype - The type of chart to render ('doughnut' or 'bar').
   * @returns {void}
   */
  public prepareDOCChart(doctype: string) {
    this.charttitle = 'Ponds harvested by DOC' + ' ' + ' ' + ' ';
    if (this.harvestlogs['pondsCount']) {
      this.charttitle += this.harvestlogs['pondsCount'] + ' ponds';
    }
    let ctx: any = document.getElementById('docmyChart');
    let ctx1: any = ctx.getContext('2d');
    // FOR NO DATA FOUND
    let isallareZeros = _.filter(this.piedata, function (o) {
      return o != 0;
    }); // if all are not zeros
    let docnoData = false;
    if (isallareZeros.length == 0) {
      docnoData = true;
    }
    // FOR NO DATA FOUND
    // FOR NO DATA FOUND
    let legenddisply = true;
    if (doctype == 'bar') {
      legenddisply = false;
    }
    this.docgraphconfig = {
      // type: 'doughnut',
      data: {
        labels: this.piekeys,
        datasets: [{
          backgroundColor: [
            '#6AD276',
            '#3498db',
            '#FAFF75',
            '#9b59b6',
            '#F0785F'
          ],
          // data: [12, 19, 3, 17, 28, 24, 7]
          data: this.piedata
        }]
      },
      options: {
        legend: {
          // display: docnoData ? false : true,
          display: legenddisply,
          labels: {
            fontColor: 'gray',
          },
          position: 'right',
        },
        title: {
          display: true,
          text: docnoData ? 'No ponds harvested' : '',
          fontSize: 15,
        },
        plugins: {
          labels: {
            render: 'value',
            fontColor: '#fff',
            fontSize: 17,
            fontStyle: 'bold',
            arc: true,
            precision: 2
          },
        },
      }
    };
    let temp = $.extend(true, {}, this.docgraphconfig);
    temp.type = doctype || 'doughnut';
    // FOR NO DATA FOUND
    this.docgraph = new Chart(ctx1, temp);
  }

  /**
   * @method docgraphselection
   * @description
   * Handles the user's selection of chart type for DOC (Bar or Doughnut).
   * Destroys the existing chart and creates a new one with the selected type.
   * Saves the preference to the database or localStorage.
   *
   * @param {string} doctype - The selected chart type.
   * @returns {void}
   */
  public docgraphselection(doctype: string) {
    if (doctype == 'bar') {
      let ele = '<canvas id=\'docmyChart\'></canvas>';
      $('#canvasarea').html(ele);
      let ctx: any = document.getElementById('docmyChart');
      let ctx1: any = ctx.getContext('2d');

      if (this.docgraph) {
        this.docgraph.destroy();
      }
      this.docgraphconfig.options.legend.display = false; //check
      let temp = $.extend(true, {}, this.docgraphconfig);
      temp.type = 'bar';
      this.docgraph = new Chart(ctx1, temp);
      if (this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('docType', temp.type);
      }
    } else if (doctype == 'doughnut') {
      let ele = '<canvas id=\'docmyChart\'></canvas>';
      $('#canvasarea').html(ele);
      let ctx: any = document.getElementById('docmyChart');
      let ctx1: any = ctx.getContext('2d');
      if (this.docgraph) {
        this.docgraph.destroy();
      }
      this.docgraphconfig.options.legend.display = true; //check
      let temp = $.extend(true, {}, this.docgraphconfig);
      temp.type = 'doughnut';
      this.docgraph = new Chart(ctx1, temp);
      if (this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('docType', temp.type);
      }
    }
  }

  /**
   * @method prepareSRChart
   * @description
   * Configures and renders the Survival Rate chart using Chart.js.
   * Supports both Doughnut and Bar chart types based on the `srtype` parameter.
   * Handles "No Data" scenarios.
   *
   * @param {string} srtype - The type of chart to render ('doughnut' or 'bar').
   * @returns {void}
   */
  public prepareSRChart(srtype: string) {
    this.srcharttitle = 'Ponds by survival rate' + ' ' + ' ' + ' ';
    if (this.harvestlogs['pondsCount']) {
      this.srcharttitle += this.harvestlogs['pondsCount'] + ' ponds';
    }
    let srctx: any = document.getElementById('srmyChart');
    let srctx1: any = srctx.getContext('2d');
    // FOR NO DATA FOUND
    let isallareZeros = _.filter(this.srpiedata, function (o) {
      return o != 0;
    }); // if all are not zeros
    let SRnoData = false;
    if (isallareZeros.length == 0) {
      SRnoData = true;
    }
    // FOR NO DATA FOUND
    let legenddisply = true;
    if (srtype == 'bar') {
      legenddisply = false;
    }
    this.srgraphconfig = {
      // type: 'doughnut',
      data: {
        labels: this.srpiekeys,
        datasets: [{
          backgroundColor: [
            '#A9A9A9',
            '#6AD276',
            '#3498db',
            '#FAFF75',
            '#9b59b6',
            '#F81C06'
          ],
          // data: [12, 19, 3, 17, 28, 24, 7]
          data: this.srpiedata
        }]
      },
      options: {
        legend: {
          // display: SRnoData ? false : true,
          display: legenddisply,
          labels: {
            fontColor: 'gray',
          },
          position: 'right',
        },
        title: {
          display: true,
          text: SRnoData ? 'No Data found' : '',
          fontSize: 15,
        },
        plugins: {
          labels: {
            render: 'value',
            fontColor: '#fff',
            fontSize: 17,
            fontStyle: 'bold',
            arc: true,
            precision: 2

          },
        },
      }
    };
    let temp = $.extend(true, {}, this.srgraphconfig);
    temp.type = srtype || 'doughnut';
    // FOR NO DATA FOUND
    this.srgraph = new Chart(srctx1, temp);
  }

  /**
   * @method srgraphselection
   * @description
   * Handles the user's selection of chart type for Survival Rate (Bar or Doughnut).
   * Destroys the existing chart and creates a new one with the selected type.
   * Saves the preference to the database or localStorage.
   *
   * @param {string} srtype - The selected chart type.
   * @returns {void}
   */
  public srgraphselection(srtype: string) {
    if (srtype == 'bar') {
      let ele = '<canvas id=\'srmyChart\'></canvas>';
      $('#srcanvasarea').html(ele);
      let srctx: any = document.getElementById('srmyChart');
      let srctx1: any = srctx.getContext('2d');
      if (this.srgraph) {
        this.srgraph.destroy();
      }
      this.srgraphconfig.options.legend.display = false; //check
      let temp = $.extend(true, {}, this.srgraphconfig);
      temp.type = 'bar';
      this.srgraph = new Chart(srctx1, temp);
      if (this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('srType', temp.type);
      }
    } else if (srtype == 'doughnut') {
      let ele = '<canvas id=\'srmyChart\'></canvas>';
      $('#srcanvasarea').html(ele);
      let srctx: any = document.getElementById('srmyChart');
      let srctx1: any = srctx.getContext('2d');
      if (this.srgraph) {
        this.srgraph.destroy();
      }
      this.srgraphconfig.options.legend.display = true; //check
      let temp = $.extend(true, {}, this.srgraphconfig);
      temp.type = 'doughnut';
      this.srgraph = new Chart(srctx1, temp);
      if (this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('srType', temp.type);
      }
    }
  }

  /**
   * @method charttypesavetoDB
   * @async
   * @description
   * Saves the user's chart type preference to the database via `PreferencesService`.
   *
   * @param {string} type - The chart type to save.
   * @returns {Promise<void>}
   */
  public async charttypesavetoDB(type: string) {
    try {
      this.preferences = await this.preferencesService.addPreferences(localStorage.businessID, localStorage.userID, type);
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method searchBusiness
   * @description
   * Filters the displayed Survival Rate list based on the `search` string.
   * Uses jQuery to toggle visibility of elements with class `searchfun`.
   * @returns {void}
   */
  public searchBusiness() {
    let value = this.search.toLowerCase();
    $('.searchfun').filter(function () {
      $(this).toggle($(this).text().toLowerCase().indexOf(value) != -1);
    });
  }

  /**
   * @method searchDocBusiness
   * @description
   * Filters the displayed DOC list based on the `docsearch` string.
   * Uses jQuery to toggle visibility of elements with class `searchfundoc`.
   * @returns {void}
   */
  public searchDocBusiness() {
    let value = this.docsearch.toLowerCase();
    $('.searchfundoc').filter(function () {
      $(this).toggle($(this).text().toLowerCase().indexOf(value) != -1);
    });
  }
}
